// client/src/components/pages/Settings/LexiStarWebhooks/EventTypePicker.tsx
//
// The event-type picker of the webhook dialog (ST-00207): "Every event", or per category all
// of it (CATEGORY.*) or single types from the LexiStar event catalogue, plus custom prefix
// patterns (ROUTE.DEVIATION_*). The value is the pattern list the API stores, kept canonical
// (normalizePatterns): `*` alone, no type a chosen glob already covers.
//
// ST-00280: each type is shown in words (utils/eventTypeLabels) with its code under it — the
// code is what the payload's `type` carries, so integrators still see it. The search finds
// either.

import { useMemo, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaTimes } from 'react-icons/fa';
import {
  CATEGORY_LABELS,
  CATEGORY_NOUNS,
  EVENT_CATEGORIES,
  WEBHOOK_EVENT_TYPES,
  coveredTypes,
  isPattern,
  matches,
  normalizePatterns,
  typesOf,
} from './webhookLogic';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';

export interface EventTypePickerProps {
  value: string[];
  onChange: (patterns: string[]) => void;
  disabled?: boolean;
}

export default function EventTypePicker({
  value,
  onChange,
  disabled = false,
}: EventTypePickerProps) {
  const patterns = useMemo(() => normalizePatterns(value), [value]);
  const everything = patterns[0] === '*';
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [custom, setCustom] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);

  const q = search.trim().toUpperCase();
  const found = (type: string): boolean =>
    !q || type.includes(q) || eventTypeLabel(type).toUpperCase().includes(q);
  const covered = coveredTypes(patterns).length;
  const customPatterns = patterns.filter(
    (p) => p !== '*' && !/^[A-Z]+\.\*$/.test(p) && !WEBHOOK_EVENT_TYPES.includes(p)
  );

  const set = (next: string[]) => onChange(normalizePatterns(next));
  const toggleType = (type: string) =>
    set(patterns.includes(type) ? patterns.filter((p) => p !== type) : [...patterns, type]);
  // Choosing CATEGORY.* makes that category's single types and narrower prefixes redundant
  // (normalizePatterns drops them).
  const toggleCategory = (category: string) => {
    const glob = `${category}.*`;
    set(patterns.includes(glob) ? patterns.filter((p) => p !== glob) : [...patterns, glob]);
  };
  const addCustom = () => {
    const p = custom.trim().toUpperCase();
    if (!isPattern(p)) {
      setCustomError('Use CATEGORY.NAME, or a prefix ending in * such as ROUTE.DEVIATION_*');
      return;
    }
    setCustomError(null);
    setCustom('');
    set([...patterns, p]);
  };

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="sr-only">Event types</legend>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={everything}
            onChange={() => set(everything ? [] : ['*'])}
          />
          Every event, including types added later
        </label>
        <span className="text-xs text-slate-500 tabular-nums" aria-live="polite">
          {covered} of {WEBHOOK_EVENT_TYPES.length} event types
        </span>
      </div>

      {!everything && (
        <>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find an event type, e.g. dispatched"
            aria-label="Find an event type"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {EVENT_CATEGORIES.map((category) => {
              const glob = `${category}.*`;
              const all = patterns.includes(glob);
              const types = typesOf(category).filter(found);
              if (q && types.length === 0) return null;
              const picked = typesOf(category).filter((t) => matches(patterns, t)).length;
              const expanded = Boolean(q) || open[category] === true;
              const label = CATEGORY_LABELS[category] || category;
              return (
                <div key={category} className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [category]: !expanded }))}
                      aria-expanded={expanded}
                      aria-controls={`webhook-types-${category}`}
                      className="flex-1 flex items-center gap-2 text-left text-sm font-medium text-slate-800"
                    >
                      {expanded ? (
                        <FaChevronDown size={10} aria-hidden="true" />
                      ) : (
                        <FaChevronRight size={10} aria-hidden="true" />
                      )}
                      {label}
                      <span className="text-xs font-normal text-slate-500 tabular-nums">
                        {picked > 0
                          ? `${picked} of ${typesOf(category).length}`
                          : `${typesOf(category).length} types`}
                      </span>
                    </button>
                    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        checked={all}
                        onChange={() => toggleCategory(category)}
                        aria-label={`All ${CATEGORY_NOUNS[category] || category} events (${glob})`}
                      />
                      All ({glob})
                    </label>
                  </div>
                  {expanded && (
                    <ul
                      id={`webhook-types-${category}`}
                      className="mt-2 ml-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1"
                    >
                      {types.map((type) => (
                        <li key={type}>
                          <label className="inline-flex items-start gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                              checked={matches(patterns, type)}
                              disabled={
                                all || (!patterns.includes(type) && matches(patterns, type))
                              }
                              onChange={() => toggleType(type)}
                              aria-label={`${eventTypeLabel(type)} (${type})`}
                            />
                            <span className="min-w-0">
                              <span className="block">{eventTypeLabel(type)}</span>
                              <span className="block text-xs font-mono text-slate-400 break-all">
                                {type}
                              </span>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <label
              htmlFor="webhook-custom-pattern"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Or a prefix pattern (advanced)
            </label>
            <div className="flex gap-2">
              <input
                id="webhook-custom-pattern"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustom();
                  }
                }}
                placeholder="ROUTE.DEVIATION_*"
                className="flex-1 px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-invalid={customError ? true : undefined}
                aria-describedby={customError ? 'webhook-custom-pattern-error' : undefined}
              />
              <button
                type="button"
                onClick={addCustom}
                className="px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Add
              </button>
            </div>
            {customError && (
              <p id="webhook-custom-pattern-error" className="mt-1 text-xs text-red-600">
                {customError}
              </p>
            )}
            {customPatterns.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2" aria-label="Prefix patterns">
                {customPatterns.map((p) => (
                  <li
                    key={p}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-xs font-mono text-slate-700"
                  >
                    {p}
                    <button
                      type="button"
                      onClick={() => set(patterns.filter((x) => x !== p))}
                      aria-label={`Remove ${p}`}
                      className="opacity-60 hover:opacity-100"
                    >
                      <FaTimes size={9} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </fieldset>
  );
}
